import * as React from "react";
import { useTranslation } from "react-i18next";

import { ImagePlusIcon } from "@/components/_organisms/tiptap/tiptap-icons/image-plus-icon";
import { Button, ButtonProps } from "@/components/_organisms/tiptap/tiptap-ui-primitive/button";
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
import { type Editor } from "@tiptap/react";

export interface ImageUploadButtonProps extends ButtonProps {
  editor?: Editor | null;
  extensionName?: string;
}

export function isImageActive(editor: Editor | null, extensionName: string): boolean {
  if (!editor) return false;
  return editor.isActive(extensionName);
}

export function insertImage(editor: Editor | null, extensionName: string): boolean {
  if (!editor) return false;

  return editor
    .chain()
    .focus()
    .insertContent({
      type: extensionName,
    })
    .run();
}

export function useImageUploadButton(
  editor: Editor | null,
  extensionName: string = "imageUpload",
  disabled: boolean = false,
) {
  const isActive = isImageActive(editor, extensionName);
  const handleInsertImage = React.useCallback(() => {
    if (disabled) return false;
    return insertImage(editor, extensionName);
  }, [editor, extensionName, disabled]);

  return {
    isActive,
    handleInsertImage,
  };
}

export const ImageUploadButton = React.forwardRef<HTMLButtonElement, ImageUploadButtonProps>(
  (
    {
      editor: providedEditor,
      extensionName = "imageUpload",
      className = "",
      disabled,
      onClick,
      children,
      ...buttonProps
    },
    ref,
  ) => {
    const { t } = useTranslation("common");
    const editor = useTiptapEditor(providedEditor);
    const { isActive, handleInsertImage } = useImageUploadButton(editor, extensionName, disabled);

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);

        if (!e.defaultPrevented && !disabled) {
          handleInsertImage();
        }
      },
      [onClick, disabled, handleInsertImage],
    );

    if (!editor || !editor.isEditable) {
      return null;
    }

    return (
      <Button
        ref={ref}
        type="button"
        className={className.trim()}
        data-style="ghost"
        data-active-state={isActive ? "on" : "off"}
        role="button"
        aria-label={t("editor.addImage")}
        aria-pressed={isActive}
        tooltip={t("editor.addImage")}
        onClick={handleClick}
        {...buttonProps}
      >
        {children || (
          <>
            <ImagePlusIcon className="tiptap-button-icon" />
            <span className="tiptap-button-text">{t("editor.image")}</span>
          </>
        )}
      </Button>
    );
  },
);

ImageUploadButton.displayName = "ImageUploadButton";

export default ImageUploadButton;
